import React, { useEffect, useRef } from 'react';
import { youTubeVideoId } from '../utils/youtube';

/**
 * Minimal shape of the bits of the YouTube IFrame API we use. Declared locally
 * because the project has no `@types/youtube` and pulling one in for four
 * methods is not worth the dependency.
 */
interface YTPlayer {
    getCurrentTime(): number;
    getDuration(): number;
    destroy(): void;
}

interface YTPlayerEvent {
    data: number;
}

interface YTNamespace {
    Player: new (
        element: HTMLElement,
        options: {
            videoId: string;
            playerVars?: Record<string, string | number>;
            events?: {
                onReady?: () => void;
                onStateChange?: (event: YTPlayerEvent) => void;
            };
        }
    ) => YTPlayer;
    PlayerState: { PLAYING: number; PAUSED: number; ENDED: number };
}

declare global {
    interface Window {
        YT?: YTNamespace;
        onYouTubeIframeAPIReady?: () => void;
    }
}

const API_SRC = 'https://www.youtube.com/iframe_api';

/** How often the watched position is sampled while playing. */
const PROGRESS_POLL_MS = 5000;

/** Resolves once the IFrame API is on the page, loading the script on first use. */
let apiPromise: Promise<YTNamespace> | null = null;

function loadYouTubeApi(): Promise<YTNamespace> {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (apiPromise) return apiPromise;

    apiPromise = new Promise<YTNamespace>((resolve, reject) => {
        // The API calls a single global callback; chain onto any existing one so
        // a host app that also embeds YouTube keeps working.
        const previous = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            previous?.();
            if (window.YT?.Player) resolve(window.YT);
            else reject(new Error('YouTube IFrame API loaded without a Player constructor'));
        };

        if (!document.querySelector(`script[src="${API_SRC}"]`)) {
            const script = document.createElement('script');
            script.src = API_SRC;
            script.async = true;
            script.onerror = () => reject(new Error('Failed to load the YouTube IFrame API'));
            document.head.appendChild(script);
        }
    });

    return apiPromise;
}

interface YouTubePlayerProps {
    videoUrl: string;
    title: string;
    /**
     * Called with the current position while playing, and once on pause/end.
     * Drives the localStorage watch progress — see `useVideoProgress`.
     */
    onProgress?: (seconds: number, duration: number) => void;
    className?: string;
}

/**
 * YouTube embed that reports watch position, so "Continue watching" and the
 * per-video progress bars have real numbers behind them. Falls back to a plain
 * iframe if the IFrame API cannot load (ad blockers, offline) — playback still
 * works, only progress tracking is lost.
 */
export const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
    videoUrl,
    title,
    onProgress,
    className = ''
}) => {
    const mountRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<YTPlayer | null>(null);
    const pollRef = useRef<number | null>(null);
    // Held in a ref so a changing callback identity never re-creates the player.
    const onProgressRef = useRef(onProgress);
    onProgressRef.current = onProgress;

    const videoId = youTubeVideoId(videoUrl);

    useEffect(() => {
        if (!videoId || !mountRef.current) return;

        let cancelled = false;
        const mount = mountRef.current;

        const stopPolling = () => {
            if (pollRef.current !== null) {
                window.clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };

        const sample = () => {
            const player = playerRef.current;
            if (!player) return;
            const duration = player.getDuration();
            if (duration > 0) onProgressRef.current?.(player.getCurrentTime(), duration);
        };

        loadYouTubeApi()
            .then((YT) => {
                if (cancelled) return;
                playerRef.current = new YT.Player(mount, {
                    videoId,
                    playerVars: { rel: 0, modestbranding: 1 },
                    events: {
                        onStateChange: (event) => {
                            if (event.data === YT.PlayerState.PLAYING) {
                                stopPolling();
                                pollRef.current = window.setInterval(sample, PROGRESS_POLL_MS);
                            } else {
                                stopPolling();
                                // Capture the position they actually stopped at.
                                sample();
                            }
                        }
                    }
                });
            })
            .catch((error) => {
                // Progress tracking is a nicety; a failed API load must not take
                // the page down, so fall back to a plain embed.
                console.warn('YouTube progress tracking unavailable:', error);
                if (cancelled || !mount) return;
                const iframe = document.createElement('iframe');
                iframe.src = `https://www.youtube.com/embed/${videoId}`;
                iframe.title = title;
                iframe.allow =
                    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
                iframe.allowFullscreen = true;
                iframe.className = 'w-full h-full';
                iframe.frameBorder = '0';
                mount.replaceChildren(iframe);
            });

        return () => {
            cancelled = true;
            stopPolling();
            playerRef.current?.destroy();
            playerRef.current = null;
        };
    }, [videoId, title]);

    if (!videoId) {
        return (
            <div
                className={`flex items-center justify-center bg-gray-100 text-sm text-gray-500 ${className}`}
            >
                Unable to load video player
            </div>
        );
    }

    // The API replaces this node with its own iframe; the wrapper keeps the size.
    return (
        <div className={`bg-gray-900 ${className}`}>
            <div ref={mountRef} className="w-full h-full" />
        </div>
    );
};

export default YouTubePlayer;
